import { useCallback, useEffect, useState } from 'react';
import type { PublicConfig } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useEvaluationDraft, normalizeKeywords } from '../../hooks/useEvaluationDraft';
import { useTaskPolling } from '../../hooks/useTaskPolling';
import { evaluationService } from '../../services/evaluation';
import { track } from '../../services/events';
import { useToast } from '../feedback/AppToast';
import { EvaluationForm } from './EvaluationForm';
import { GenerationStatus } from './GenerationStatus';

export function EvaluationPanel({
  config,
  onViewReports
}: {
  config: PublicConfig;
  onViewReports: (reportId?: string) => void;
}) {
  const auth = useAuth();
  const toast = useToast();
  const draftState = useEvaluationDraft(config);
  const [submitting, setSubmitting] = useState(false);
  const [pendingAfterLogin, setPendingAfterLogin] = useState(false);
  const [taskId, setTaskId] = useState<string>();
  const polling = useTaskPolling(taskId);
  const create = useCallback(async () => {
    if (!draftState.validate()) {
      toast('请先完善表单中的必填项', 'error');
      return;
    }
    if (!auth.user) {
      setPendingAfterLogin(true);
      auth.openAuth();
      toast('登录后将自动继续提交', 'info');
      return;
    }
    setSubmitting(true);
    track('report_create_click');
    try {
      const value = draftState.draft;
      const result = await evaluationService.create({
        mode: value.mode,
        task_name: value.task_name.trim(),
        keywords_text: normalizeKeywords(value.keywords_text),
        article_a: { title: value.article_a.title.trim(), body: value.article_a.body.trim() },
        article_b:
          value.mode === 'compare'
            ? { title: value.article_b.title.trim(), body: value.article_b.body.trim() }
            : null
      });
      setTaskId(result.task_id);
      draftState.clear();
      setPendingAfterLogin(false);
      track('report_create_success', {}, result.task_id);
      toast(`任务已创建，今日剩余 ${result.remaining_units} 额度`, 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : '提交失败，请重试', 'error');
    } finally {
      setSubmitting(false);
    }
  }, [auth, draftState, toast]);
  useEffect(() => {
    if (auth.user && pendingAfterLogin && !submitting) void create();
  }, [auth.user, pendingAfterLogin]);
  useEffect(() => {
    if (!polling.task) return;
    if (polling.task.status === 'success' || polling.task.status === 'partial_failed')
      track('report_completed', {}, polling.task.task_id);
    if (polling.task.status === 'failed' || polling.task.status === 'timeout')
      track('report_failed', {}, polling.task.task_id);
  }, [polling.task?.status]);
  return (
    <div id="evaluation-tool" className="container">
      <EvaluationForm
        value={draftState.draft}
        errors={draftState.errors}
        config={config}
        submitting={submitting}
        onChange={draftState.setDraft}
        onNormalize={draftState.normalize}
        onSubmit={create}
      />
      <GenerationStatus
        task={polling.task}
        elapsed={polling.elapsed}
        error={polling.error}
        onViewReports={() => onViewReports(taskId)}
      />
    </div>
  );
}
